import React, { useEffect, useState } from 'react';
import { useSetRecoilState } from 'recoil';
import { TablePaginationConfig } from 'antd';
import { ColumnType } from 'antd/lib/table';
import { useUpdateEffect } from 'usehooks-ts';

import './index.less';

import {
  ELMA365ApplicationIdentityTypeEnum,
  ELMA365ProcessIdentityTypeEnum,
  ELMA365ToolAction,
  ELMA365ToolAvailability,
  ELMA365ToolModel,
  ELMA365ToolVisibility,
} from '../../../../../../gamma-api';
import { alertsSelectorAdd } from '../../../../../recoil';
import { defaultApi } from '../../../../../apis';
import IbDebounceInput from '../../../../../components/common/IbDebounceInput';
import IbPagination from '../../../../../components/common/IbPagination';
import IbTable from '../../../../../components/common/IbTable';
import IbTypography from '../../../../../components/common/IbTypography';
import IbTextHighlighter from '../../../../../components/common/IbTextHighlighter';
import IbIcon from '../../../../../components/common/IbIcon';
import IbCheckbox from '../../../../../components/common/IbCheckbox';
import { ELMA365_TOOL_ACTION_ICON_NAMES, ELMA365_COMPONENT_TYPE_ICON_NAMES } from '../../const';
import { getTranslateFunc } from '../../../../../utils/stringUtil';

import {
  LABEL_CLASS_NAME,
  MAIN_CLASS_NAME,
  TOOL_PAGE_SIZE,
  SEARCH_CLASS_NAME,
  CHECKBOX_CLASS_NAME,
  TOOL_ICON_SIZE,
  COMPONENT_CLASS_NAME,
} from './const';

interface IToolTableProps {
  versionId?: string;
  toggledIds?: { [key: string]: boolean };
  onToggle?: (id: string, selected: boolean) => void;
}

const ToolTable: React.FC<IToolTableProps> = ({ versionId, toggledIds, onToggle }) => {
  const addAlert = useSetRecoilState(alertsSelectorAdd);

  const [pagination, setPagination] = useState<TablePaginationConfig>({
    current: 1,
    pageSize: TOOL_PAGE_SIZE,
    total: 0,
  });

  const [searchText, setSearchText] = useState('');
  const [tools, setTools] = useState<ELMA365ToolModel[]>([]);

  const [loading, setLoading] = useState(true);

  const translate = getTranslateFunc('Toolkits: ELMA365: Tools:');

  const isToolSelected = (tool: ELMA365ToolModel) => {
    return toggledIds?.[tool.tool_id] ?? tool.availability === ELMA365ToolAvailability.Enabled;
  };

  const loadTools = async () => {
    if (!versionId) {
      setTools([]);
      return;
    }

    setLoading(true);

    try {
      const response = await defaultApi.searchELMA365ToolkitTools(
        (pagination.current || 1) - 1,
        TOOL_PAGE_SIZE,
        true,
        searchText,
        undefined,
        undefined,
        [versionId],
        undefined,
        undefined,
        undefined,
        [ELMA365ToolVisibility.Visible]
      );
      setTools(response.data.items || []);
      setPagination((prev) => ({ ...prev, total: response.data.total_item_count || 0 }));
    } catch (err) {
      addAlert({
        content: translate('Failed to load'),
        type: 'error',
      });
      // eslint-disable-next-line no-console
      console.error(err);
    }

    setLoading(false);
  };

  useEffect(() => {
    loadTools().finally();
  }, [versionId, pagination.current, pagination.pageSize, searchText]);

  useUpdateEffect(() => {
    if (pagination.current !== 1) {
      setPagination({ ...pagination, current: 1 });
    }
  }, [versionId, searchText]);

  const onPaginationChange = (page: number) => setPagination({ ...pagination, current: page });

  const onSearchTextChange = (value: string) => setSearchText(value);

  const onToolToggle = (tool: ELMA365ToolModel) => {
    onToggle?.(tool.tool_id, !isToolSelected(tool));
  };

  const renderSearch = () => {
    return (
      <div className={SEARCH_CLASS_NAME}>
        <IbDebounceInput
          allowClear
          disabled={!versionId}
          placeholder={translate('Search: Placeholder')}
          prefix={<IbIcon iconName="search" />}
          onDebouncedChange={onSearchTextChange}
        />
      </div>
    );
  };

  const renderType = (type: ELMA365ApplicationIdentityTypeEnum | ELMA365ProcessIdentityTypeEnum) => {
    return <IbIcon iconName={ELMA365_COMPONENT_TYPE_ICON_NAMES[type]} size={TOOL_ICON_SIZE} />;
  };

  const renderAction = (action: ELMA365ToolAction) => {
    return <IbIcon iconName={ELMA365_TOOL_ACTION_ICON_NAMES[action]} size={TOOL_ICON_SIZE} />;
  };

  const renderCheckbox = (tool: ELMA365ToolModel) => {
    const onCheckboxChange = () => onToolToggle(tool);

    return (
      <div className={CHECKBOX_CLASS_NAME}>
        <IbCheckbox value={isToolSelected(tool)} onChange={onCheckboxChange} />
        <div className={LABEL_CLASS_NAME}>
          {renderAction(tool.action)}
          <IbTypography.Paragraph ellipsis type="secondary">
            <IbTextHighlighter highlightedText={searchText} text={tool.attributes.name.value} />
          </IbTypography.Paragraph>
        </div>
      </div>
    );
  };

  const renderComponent = (tool: ELMA365ToolModel) => {
    return (
      <div className={COMPONENT_CLASS_NAME}>
        {renderType(tool.component.identity.type)}
        <IbTypography.Paragraph ellipsis type="secondary">
          <IbTextHighlighter highlightedText={searchText} text={tool.component.identity.name} />
        </IbTypography.Paragraph>
      </div>
    );
  };

  const renderTable = () => {
    const columns: ColumnType<ELMA365ToolModel>[] = [
      {
        title: translate('Table: Name'),
        width: '36%',
        ellipsis: true,
        render: (_, tool) => renderCheckbox(tool),
      },
      {
        title: translate('Table: Component'),
        width: '32%',
        ellipsis: true,
        render: (_, tool) => renderComponent(tool),
      },
      {
        title: translate('Table: Namespace'),
        width: '32%',
        ellipsis: true,
        render: (_, tool) => (
          <IbTypography.Paragraph ellipsis type="secondary">
            <IbTextHighlighter highlightedText={searchText} text={tool.namespace.identity.name} />
          </IbTypography.Paragraph>
        ),
      },
    ];

    return (
      <IbTable
        columns={columns}
        dataSource={tools}
        emptyText={<IbTypography.Paragraph disabled>{translate('Nothing found')}</IbTypography.Paragraph>}
        loading={!versionId || loading}
        rowKey={(tool: ELMA365ToolModel) => tool.tool_id}
        size="middle"
        onRowClick={onToolToggle}
      />
    );
  };

  const renderPagination = () => {
    if ((pagination.total ?? 0) <= TOOL_PAGE_SIZE) {
      return null;
    }

    return (
      <IbPagination
        current={pagination.current}
        defaultCurrent={1}
        defaultPageSize={TOOL_PAGE_SIZE}
        pageSize={TOOL_PAGE_SIZE}
        showSizeChanger={false}
        total={pagination.total}
        onChange={onPaginationChange}
      />
    );
  };

  return (
    <div className={MAIN_CLASS_NAME}>
      {renderSearch()}
      {renderTable()}
      {renderPagination()}
    </div>
  );
};

export default ToolTable;
